import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface ConsultationOption {
  id: string;
  title: string;
  description: string;
  duration: string;
  icon: string;
  recommended: boolean;
}

interface ConsultationOptionsProps {
  selectedOption: string;
  onOptionSelect: (optionId: string) => void;
  className?: string;
}

const consultationOptions: ConsultationOption[] = [
  {
    id: 'strategic-briefing',
    title: 'Executive Strategic Briefing',
    description: 'Comprehensive overview of AI orchestration opportunities tailored to your organization\'s strategic objectives and risk profile.',
    duration: '90 minutes',
    icon: 'PresentationChartLineIcon',
    recommended: true,
  },
  {
    id: 'technical-deep-dive',
    title: 'Technical Implementation Review',
    description: 'Detailed examination of platform capabilities, security frameworks, and integration methodology with your IT leadership team.',
    duration: '2 hours',
    icon: 'CpuChipIcon',
    recommended: false,
  },
  {
    id: 'compliance-consultation',
    title: 'Compliance & Governance Framework',
    description: 'Focused discussion on regulatory requirements, risk mitigation strategies, and governance protocols for AI adoption.',
    duration: '60 minutes',
    icon: 'ShieldCheckIcon',
    recommended: false,
  },
  {
    id: 'roi-assessment',
    title: 'ROI & Business Case Workshop',
    description: 'Collaborative session to quantify potential business impact and develop investment justification for AI orchestration.',
    duration: '2 hours',
    icon: 'ChartBarIcon',
    recommended: false,
  },
];

const ConsultationOptions = ({ selectedOption, onOptionSelect, className = '' }: ConsultationOptionsProps) => {
  return (
    <div className={`space-y-4 ${className}`}>
      <div className="mb-6">
        <h3 className="text-xl font-display font-semibold text-foreground mb-2">
          Select Consultation Type
        </h3>
        <p className="text-sm text-muted-foreground">
          Choose the consultation format that best aligns with your evaluation stage and decision-making needs.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {consultationOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onOptionSelect(option.id)}
            className={`relative p-6 rounded-lg border-2 text-left transition-all duration-300 hover:shadow-md ${
              selectedOption === option.id
                ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50'
            }`}
          >
            {option.recommended && (
              <div className="absolute top-4 right-4">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                  Recommended
                </span>
              </div>
            )}

            <div className="flex items-start space-x-4">
              <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                selectedOption === option.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                <Icon name={option.icon as any} size={24} />
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="text-base font-display font-semibold text-foreground mb-2">
                  {option.title}
                </h4>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  {option.description}
                </p>
                <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                  <Icon name="ClockIcon" size={14} />
                  <span>{option.duration}</span>
                </div>
              </div>
            </div>

            <div className={`absolute inset-0 rounded-lg pointer-events-none ${
              selectedOption === option.id ? 'ring-2 ring-primary ring-offset-2' : ''
            }`}></div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ConsultationOptions;